import {Track, type RemoteTrackPublication, type Room} from 'livekit-client';
import {CameraResumeError, type ReliefSnapshot, type VideoReliefAdapter, type VideoState} from './videoReliefController';

interface LivekitReliefDeps {
    readonly getRoom: () => Room | undefined;
    readonly setCamera: (enabled: boolean) => Promise<boolean>;
}

interface OverrideHolder {
    requestedDisabled?: boolean;
    emitTrackUpdate?: () => void;
}

export const clearEnabledOverride = (publication: RemoteTrackPublication): void => {
    const holder = publication as unknown as OverrideHolder;
    if ('requestedDisabled' in holder && typeof holder.emitTrackUpdate === 'function') {
        holder.requestedDisabled = undefined;
        holder.emitTrackUpdate();
        return;
    }
    publication.setEnabled(true);
};

const remoteCameras = (room: Room): RemoteTrackPublication[] => {
    const out: RemoteTrackPublication[] = [];
    room.remoteParticipants.forEach(participant => {
        const publication = participant.getTrackPublication(Track.Source.Camera);
        if (publication && publication.kind === Track.Kind.Video) {
            out.push(publication as RemoteTrackPublication);
        }
    });
    return out;
};

const priorOverride = (publication: RemoteTrackPublication): boolean | undefined =>
    (publication as unknown as OverrideHolder).requestedDisabled;

const restoreOverride = (publication: RemoteTrackPublication, prior: boolean | undefined): void => {
    if (prior === undefined) {
        clearEnabledOverride(publication);
    } else if (prior === false) {
        publication.setEnabled(true);
    }
};

const liveVideo = (publication: RemoteTrackPublication): boolean => publication.isSubscribed && !publication.isMuted;

export const createLivekitReliefAdapter = ({getRoom, setCamera}: LivekitReliefDeps): VideoReliefAdapter => {
    const disabled = new Map<RemoteTrackPublication, boolean | undefined>();

    const disableNew = (room: Room): void => {
        remoteCameras(room).forEach(publication => {
            if (liveVideo(publication) && !disabled.has(publication)) {
                disabled.set(publication, priorOverride(publication));
                publication.setEnabled(false);
            }
        });
    };

    const read = (): VideoState => {
        const room = getRoom();
        if (!room) {
            return {cameraOn: false, remoteVideoOn: false};
        }
        return {
            cameraOn: room.localParticipant.isCameraEnabled,
            remoteVideoOn: remoteCameras(room).some(publication => liveVideo(publication) && publication.isEnabled),
        };
    };

    const pause = async (): Promise<ReliefSnapshot> => {
        const room = getRoom();
        if (!room) {
            throw new Error('Call is not active');
        }
        const cameraWasOn = room.localParticipant.isCameraEnabled;
        if (cameraWasOn && !(await setCamera(false))) {
            throw new Error('Camera did not turn off');
        }
        disableNew(room);
        return {cameraWasOn, remoteVideoWasOn: disabled.size > 0};
    };

    const resume = async (snapshot: ReliefSnapshot): Promise<void> => {
        const room = getRoom();
        if (!room) {
            return;
        }
        try {
            if (snapshot.cameraWasOn && !room.localParticipant.isCameraEnabled && !(await setCamera(true))) {
                throw new CameraResumeError('Camera did not turn on');
            }
        } catch (error) {
            throw error instanceof CameraResumeError ? error : new CameraResumeError(error instanceof Error ? error.message : String(error));
        } finally {
            [...disabled].forEach(([publication, prior]) => {
                restoreOverride(publication, prior);
                disabled.delete(publication);
            });
        }
    };

    const sync = (): void => {
        const room = getRoom();
        if (room) {
            disableNew(room);
        }
    };

    return {read, pause, resume, sync, holdsRemote: () => disabled.size > 0};
};
